import React, { useEffect, useMemo, useRef, useState } from "react";
import * as d3 from "d3-geo";
import { feature } from "topojson-client";
import world110m from "world-atlas/countries-110m.json";

/**
 * WorldMosaicMVP – Logo-Farbwelt
 * Navy/Gold inspiriert vom Logo:
 *  - bgDark:  sehr dunkles Navy (Seite)
 *  - bgNavy:  Navy (Kartenhintergrund)
 *  - gold:    Akzent/Rahmen
 *  - goldSoft: dezenteres Gold für Punkte
 *  - ink:     helle Schrift
 */
const COLORS = {
  bgDark: "#0B1E3F",    // Seitenhintergrund
  bgNavy: "#091A33",    // Kartenfläche
  sphere: "#08162C",    // Erd-Kugel/Meer
  gold: "#C9A227",      // Akzent
  goldSoft: "#D6B75B",  // Punkte (leer)
  ink: "#EDEBE6",       // Text
};

export default function WorldMosaicMVP() {
  const WIDTH = 1200;
  const HEIGHT = 680;
  const STEP_DEG = 3; // Rasterauflösung
  const DOT = 8;      // Punktgröße

  const projection = useMemo(
    () => d3.geoNaturalEarth1().fitExtent([[20, 20], [WIDTH - 20, HEIGHT - 20]], { type: "Sphere" } as any),
    [WIDTH, HEIGHT]
  );
  const path = useMemo(() => d3.geoPath(projection), [projection]);

  const [cells, setCells] = useState<Cell[]>([]);
  const [hover, setHover] = useState<{ cell: Cell; x: number; y: number } | null>(null);
  const [selected, setSelected] = useState<Cell | null>(null);
  const [showBuy, setShowBuy] = useState<Cell | null>(null);

  // Welt-Landflächen laden & Raster erzeugen
  useEffect(() => {
    const land = feature(world110m as any, (world110m as any).objects.land);
    const generated: Cell[] = [];

    for (let lat = 85; lat >= -85; lat -= STEP_DEG) {
      for (let lon = -180; lon <= 180; lon += STEP_DEG) {
        const pt: [number, number] = [lon, lat];
        // @ts-ignore d3.geoContains exists at runtime
        const onLand = d3.geoContains(land as any, pt);
        if (!onLand) continue;
        const p = projection(pt); if (!p) continue;
        const [x, y] = p;

        generated.push({
          id: `${lon}_${lat}`,
          lon, lat, x, y,
          status: Math.random() < 0.08 ? "APPROVED" : "EMPTY",
          priceCents: priceFor(lat, lon),
          imageThumbUrl: undefined,
          imageFullUrl: undefined,
          hoverText: undefined,
          title: undefined,
        });
      }
    }

    // ein paar Demo-Bilder
    const thumbs = [
      "https://images.unsplash.com/photo-1503023345310-bd7c1de61c7d?q=80&w=400&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1520975938310-4487bca9e089?q=80&w=400&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1520975922213-8bdf0f12bc1b?q=80&w=400&auto=format&fit=crop",
    ];

    const filled = generated.map((c, i) =>
      c.status === "APPROVED"
        ? {
            ...c,
            imageThumbUrl: thumbs[i % thumbs.length],
            imageFullUrl: thumbs[i % thumbs.length].replace("w=400", "w=1600"),
            hoverText: `Greetings from ${approxRegion(c.lat, c.lon)}!`,
            title: `Example #${i}`,
          }
        : c
    );

    setCells(filled);
  }, []);

  // Tooltip-Position tracken
  const svgRef = useRef<SVGSVGElement | null>(null);
  const onMouseMove = (e: React.MouseEvent) => {
    if (!svgRef.current || !hover) return;
    const rect = svgRef.current.getBoundingClientRect();
    setHover({ cell: hover.cell, x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bgDark, color: COLORS.ink }}>
      <div className="max-w-6xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-semibold mb-2" style={{ color: COLORS.ink }}>
          World Map Mosaic – MVP (Logo-Farben)
        </h1>
        <p className="mb-4" style={{ color: "rgba(237,235,230,0.85)" }}>
          Hover über leere Felder zeigt den Preis (Gold). Klick auf belegte Felder öffnet ein großes Bild.
        </p>

        <div
          className="relative rounded-2xl overflow-hidden border"
          style={{ borderColor: "rgba(201,162,39,0.35)", background: COLORS.bgNavy }}
        >
          <svg
            ref={svgRef}
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="w-full h-[min(70vh,720px)] block"
            onMouseMove={onMouseMove}
          >
            {/* Hintergrund/Ozean */}
            <rect x={0} y={0} width={WIDTH} height={HEIGHT} fill={COLORS.bgNavy} />
            {/* Erdkugel (dezent) */}
            <path d={path({ type: "Sphere" } as any) || undefined} fill={COLORS.sphere} />

            {/* Zellen */}
            {cells.map((c) => (
              <g
                key={c.id}
                onMouseEnter={(e) => {
                  const rect = svgRef.current?.getBoundingClientRect();
                  const x = e.clientX - (rect?.left ?? 0);
                  const y = e.clientY - (rect?.top ?? 0);
                  setHover({ cell: c, x, y });
                }}
                onMouseLeave={() => setHover(null)}
                onClick={() => (c.status === "EMPTY" ? setShowBuy(c) : setSelected(c))}
                style={{ cursor: "pointer" }}
              >
                {c.status === "APPROVED" && c.imageThumbUrl ? (
                  <>
                    <defs>
                      <clipPath id={`clip_${c.id}`}>
                        <circle cx={c.x} cy={c.y} r={DOT / 2} />
                      </clipPath>
                    </defs>
                    <image
                      href={c.imageThumbUrl}
                      x={c.x - DOT / 2}
                      y={c.y - DOT / 2}
                      width={DOT}
                      height={DOT}
                      preserveAspectRatio="xMidYMid slice"
                      clipPath={`url(#clip_${c.id})`}
                    />
                    <circle
                      cx={c.x}
                      cy={c.y}
                      r={DOT / 2}
                      fill="transparent"
                      stroke={COLORS.gold}
                      strokeOpacity={0.6}
                      strokeWidth={0.7}
                    />
                  </>
                ) : (
                  <circle
                    cx={c.x}
                    cy={c.y}
                    r={DOT / 3}
                    fill={COLORS.goldSoft}
                    opacity={0.9}
                    stroke={COLORS.gold}
                    strokeOpacity={0.25}
                    strokeWidth={0.5}
                  />
                )}
              </g>
            ))}
          </svg>

          {/* Tooltip */}
          {hover && (
            <div
              className="absolute z-20 px-3 py-2 rounded-xl text-sm shadow-xl"
              style={{
                left: hover.x + 12,
                top: hover.y + 12,
                background: "rgba(9,26,51,0.92)",
                border: `1px solid ${COLORS.gold}55`,
                color: COLORS.ink,
              }}
            >
              {hover.cell.status === "EMPTY"
                ? `Preis: € ${(hover.cell.priceCents / 100).toFixed(2)}`
                : hover.cell.hoverText || "—"}
            </div>
          )}
        </div>

        <p className="mt-3 text-xs" style={{ color: "rgba(237,235,230,0.6)" }}>
          Hinweis: Reines Frontend-Mock. Backend/Stripe/Moderation folgen im nächsten Schritt.
        </p>
      </div>

      {/* Bild-Modal */}
      {selected && (
        <div
          className="fixed inset-0 grid place-items-center p-4 z-50"
          style={{ background: "rgba(0,0,0,0.6)" }}
          onClick={() => setSelected(null)}
        >
          <div
            className="rounded-2xl p-4 w-[min(900px,95vw)]"
            style={{ background: COLORS.bgNavy, border: `1px solid ${COLORS.gold}55` }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold" style={{ color: COLORS.ink }}>
                {selected.title || "Beitragsbild"}
              </h3>
              <button
                className="px-3 py-1 rounded-lg"
                style={{ background: COLORS.sphere, color: COLORS.ink, border: `1px solid ${COLORS.gold}44` }}
                onClick={() => setSelected(null)}
              >
                Schließen
              </button>
            </div>
            {selected.imageFullUrl ? (
              <img
                src={selected.imageFullUrl}
                alt="Beitragsbild"
                className="max-h-[65vh] w-full object-contain rounded-lg"
              />
            ) : (
              <div className="h-64 grid place-items-center rounded-lg" style={{ background: COLORS.sphere }}>
                Kein Bild
              </div>
            )}
            <div className="mt-3 text-sm" style={{ color: "rgba(237,235,230,0.85)" }}>
              {selected.hoverText || "Kein Text hinterlegt"}
            </div>
          </div>
        </div>
      )}

      {/* Kauf-Dialog (Demo) */}
      {showBuy && (
        <div
          className="fixed inset-0 grid place-items-center p-4 z-50"
          style={{ background: "rgba(0,0,0,0.6)" }}
          onClick={() => setShowBuy(null)}
        >
          <div
            className="rounded-2xl p-4 w-[min(560px,95vw)]"
            style={{ background: COLORS.bgNavy, border: `1px solid ${COLORS.gold}55` }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2" style={{ color: COLORS.ink }}>
              Feld kaufen
            </h3>
            <p className="text-sm mb-4" style={{ color: "rgba(237,235,230,0.85)" }}>
              Position: {showBuy.lat.toFixed(1)}°, {showBuy.lon.toFixed(1)}° · Preis: €
              {(showBuy.priceCents / 100).toFixed(2)}
            </p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 rounded-xl"
                style={{ background: COLORS.sphere, color: COLORS.ink, border: `1px solid ${COLORS.gold}44` }}
                onClick={() => setShowBuy(null)}
              >
                Abbrechen
              </button>
              <button
                className="px-4 py-2 rounded-xl font-semibold"
                style={{ background: COLORS.gold, color: "#151515" }}
                onClick={() => {
                  alert("In Schritt 2 leiten wir zu Stripe um.");
                  setShowBuy(null);
                }}
              >
                Zur Kasse (Demo)
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ——— Types & Helpers ——— */

type Status = "EMPTY" | "APPROVED";

type Cell = {
  id: string;
  lon: number; lat: number; x: number; y: number;
  status: Status;
  priceCents: number;
  imageThumbUrl?: string; imageFullUrl?: string; hoverText?: string; title?: string;
};

function priceFor(lat: number, lon: number) {
  const base = 199; // 1,99 €
  const band = 1 + 0.4 * Math.exp(-Math.pow(lat / 40, 2));
  const jitter = 0.9 + Math.random() * 0.2;
  return Math.round(base * band * jitter);
}

function approxRegion(lat: number, lon: number) {
  if (lat > 35 && lon > -10 && lon < 40) return "Europe";
  if (lat > 10 && lon < -30) return "the Americas";
  if (lat > -40 && lon > 60) return "Asia";
  if (lat < -10 && lon > 110) return "Australia";
  if (lat < 15 && lon > 10 && lon < 40) return "Africa";
  return "the world";
}